import { supabase, getSelectedOrgId } from './supabaseClient';
import { createClient } from '@supabase/supabase-js'; // Import createClient for temp instance
import { Profile } from './usersService';

export interface Patient {
    id: string;
    profile_id: string;
    profile?: Profile;
    reprocann_number?: string;
    reprocann_status: 'active' | 'pending' | 'expired' | 'rejected';
    expiration_date?: string;
    monthly_limit: number;
    notes?: string;

    // New Fields
    date_of_birth?: string;
    pathology?: string;
    phone?: string;
    address?: string;
    document_number?: string;
    reprocann_issue_date?: string;
    file_reprocann_url?: string;
    file_reprocann_back_url?: string;
    file_dni_front_url?: string;
    file_dni_back_url?: string;
    file_affidavit_url?: string; // Declaracion Jurada
    file_consent_url?: string;   // Consentimiento Bilateral
    is_approved_by_org?: boolean;
    follows_treatment?: boolean;

    created_at?: string;
}

export const patientsService = {
    // Get all patients with their profile data
    async getPatients(): Promise<Patient[]> {
        if (!supabase) return [];

        const { data, error } = await supabase
            .from('aurora_patients')
            .select('*, profile:profiles(*)')
            .eq('organization_id', getSelectedOrgId())
            .order('created_at', { ascending: false });

        if (error) {
            console.error('Error fetching patients:', error);
            return [];
        }

        // Map the joined profile correctly
        return data.map((p: any) => ({
            ...p,
            profile: p.profile // Supabase returns the joined relation as a property
        }));
    },

    // Get a single patient by their aurora_patients ID
    async getPatientById(id: string): Promise<Patient | null> {
        if (!supabase) return null;

        const { data, error } = await supabase
            .from('aurora_patients')
            .select('*, profile:profiles(*)')
            .eq('id', id)
            .single();

        if (error) {
            console.error('Error fetching patient:', error);
            return null;
        }

        return data;
    },

    // Create or Update a patient record
    // Note: Since it's 1:1 with Profile, we usually create it when "promoting" a user to patient
    async upsertPatient(patient: Partial<Patient>): Promise<Patient | null> {
        if (!supabase) return null;

        const { data: { user } } = await supabase.auth.getUser();

        const payload: any = {
            ...patient,
            created_by: user?.id,
            updated_at: new Date().toISOString(),
            organization_id: getSelectedOrgId()
        };

        // Remove joined profile object if present to avoid errors
        delete payload.profile;
        delete payload.created_at;

        if (patient.id) {
            const { data, error } = await supabase
                .from('aurora_patients')
                .update(payload)
                .eq('id', patient.id)
                .select()
                .single();

            if (error) {
                console.error('Error updating patient:', error);
                throw error;
            }

            return data;
        } else {
            const { data, error } = await supabase
                .from('aurora_patients')
                .upsert(payload, { onConflict: 'profile_id' })
                .select()
                .single();

            if (error) {
                console.error('Error saving patient:', error);
                throw error;
            }

            return data;
        }
    },

    // Specific method to create a patient from an existing user profile
    async createPatientFromProfile(profileId: string): Promise<boolean> {
        if (!supabase) return false;

        // Check if already exists
        const { data: existing } = await supabase
            .from('aurora_patients')
            .select('id')
            .eq('profile_id', profileId)
            .maybeSingle();

        if (existing) return true; // Already a patient

        const { error } = await supabase
            .from('aurora_patients')
            .insert([{
                profile_id: profileId,
                reprocann_status: 'pending',
                monthly_limit: 40 // Default
            }]);

        if (error) {
            console.error('Error creating patient:', error);
            return false;
        }
        return true;
    },

    // Calculate consumption for the current month
    async getPatientConsumption(patientId: string): Promise<{ current: number, limit: number }> {
        if (!supabase) return { current: 0, limit: 0 };

        // 1. Get Limit
        const { data: patient } = await supabase
            .from('aurora_patients')
            .select('monthly_limit, profile_id')
            .eq('organization_id', getSelectedOrgId())
            .eq('profile_id', patientId)
            .single();

        if (!patient) return { current: 0, limit: 0 };

        // 2. Calculate Consumption (Current Month)
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);

        const { data: movements, error } = await supabase
            .from('chakra_dispensary_movements')
            .select('amount')
            .eq('member_id', patientId)
            .eq('type', 'dispense')
            .gte('created_at', startOfMonth.toISOString());

        if (error) {
            console.error("Error fetching consumption:", error);
            return { current: 0, limit: patient.monthly_limit };
        }

        // Sum absolute amounts (amounts are stored negative for dispense)
        const totalDispensed = movements.reduce((acc, m) => acc + Math.abs(m.amount), 0);

        return {
            current: totalDispensed,
            limit: patient.monthly_limit
        };
    },

    // Upload a document to patient_docs bucket
    async uploadDocument(file: File, path: string): Promise<string | null> {
        if (!supabase) return null;

        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${path}/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from('patient_docs')
            .upload(filePath, file);

        if (uploadError) {
            console.error('Error uploading file:', uploadError);
            throw uploadError;
        }

        // Get Public URL
        const { data: { publicUrl } } = supabase.storage
            .from('patient_docs')
            .getPublicUrl(filePath);

        return publicUrl;

    },

    // Delete a patient record
    async deletePatient(id: string): Promise<boolean> {
        if (!supabase) return false;

        const { error } = await supabase
            .from('aurora_patients')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting patient:', error);
            return false;
        }
        return true;
    },

    // Register a NEW patient (creates Auth User + Patient Record with custom club welcome email)
    async registerNewPatient(
        patientData: Partial<Patient>, 
        userData: { email: string, fullName: string, password?: string, documentNumber?: string },
        sendWelcomeEmail: boolean = true
    ): Promise<Patient | null> {
        if (!supabase) return null;

        const currentOrgId = getSelectedOrgId();
        if (!currentOrgId) {
            throw new Error("No hay una organización activa seleccionada.");
        }

        // 1. Invoke the dedicated Edge Function
        // This creates the user securely via Supabase Admin API with email_confirm: true (so Supabase does NOT send generic "Bienvenido a TrazAPP")
        // and optionally dispatches the club-branded welcome email from the specific organization.
        const { data, error } = await supabase.functions.invoke('register-patient', {
            body: {
                organizationId: currentOrgId,
                userData,
                patientData,
                sendWelcomeEmail
            }
        });

        if (error) {
            console.error("Error from register-patient Edge Function:", error);
            throw new Error(error.message || "Error al registrar paciente");
        }

        if (data?.error) {
            throw new Error(data.error);
        }

        return data?.patient || null;
    },

    // ==========================================
    // Clinical Module Methods
    // ==========================================

    async getClinicalAdmission(patientId: string): Promise<any | null> {
        if (!supabase) return null;

        const { data, error } = await supabase
            .from('clinical_admissions')
            .select('*')
            .eq('organization_id', getSelectedOrgId())
            .eq('patient_id', patientId)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

        if (error && error.code !== 'PGRST116') { // Ignore "no rows" error
            console.error("Error fetching clinical admission:", error);
        }
        return data || null;
    },

    async createClinicalAdmission(admission: any): Promise<any | null> {
        if (!supabase) return null;

        // Generate simple hash if not provided (server-side would be better but MVP logic here)
        // We'll use a simple random string for "anonymized" view if not generated.
        // Real HIPAA compliance requires meaningful irreversible hash or tokenization service.
        const hash = admission.patient_hash || Math.random().toString(36).substring(7);

        const { data, error } = await supabase
            .from('clinical_admissions')
            .insert([{ ...admission, patient_hash: hash, organization_id: getSelectedOrgId() }])
            .select()
            .single();

        if (error) {
            console.error("Error creating admission:", error);
            throw error;
        }
        return data;
    },

    async getEvolutions(admissionId: string): Promise<any[]> {
        if (!supabase) return [];

        const { data, error } = await supabase
            .from('clinical_evolutions')
            .select('*')
            .eq('organization_id', getSelectedOrgId())
            .eq('admission_id', admissionId)
            .order('created_at', { ascending: false }); // Newest first

        if (error) {
            console.error("Error fetching evolutions:", error);
            return [];
        }
        return data;
    },

    async addEvolution(evolution: any): Promise<any | null> {
        if (!supabase) return null;

        const { data, error } = await supabase
            .from('clinical_evolutions')
            .insert([{
                ...evolution,
                organization_id: getSelectedOrgId()
            }])
            .select()
            .single();

        if (error) {
            console.error("Error adding evolution:", error);
            throw error;
        }
        return data;
    },

    async updateEvolution(id: string, updates: any): Promise<any | null> {
        if (!supabase) return null;

        // Clean out id or admission_id if inadvertently passed
        const payload = { ...updates };
        delete payload.id;
        delete payload.admission_id;

        // Attempt 1: Native columns update
        const { data, error } = await supabase
            .from('clinical_evolutions')
            .update(payload)
            .eq('id', id)
            .select()
            .single();

        if (error) {
            // Fallback in case updated_at / updated_by / edit_history columns are not yet in the DB table
            if (error.code === '42703' && (payload.updated_at || payload.updated_by || payload.edit_history)) {
                console.warn("Audit columns missing in DB table, applying fallback into template_data._audit");
                const fallbackPayload = { ...payload };
                const currentTemplateData = fallbackPayload.template_data || {};
                fallbackPayload.template_data = {
                    ...currentTemplateData,
                    _audit: {
                        updated_at: payload.updated_at,
                        updated_by: payload.updated_by,
                        edit_history: payload.edit_history
                    }
                };
                delete fallbackPayload.updated_at;
                delete fallbackPayload.updated_by;
                delete fallbackPayload.edit_history;

                const { data: retryData, error: retryError } = await supabase
                    .from('clinical_evolutions')
                    .update(fallbackPayload)
                    .eq('id', id)
                    .select()
                    .single();

                if (retryError) {
                    console.error("Error updating evolution (fallback):", retryError);
                    throw retryError;
                }
                return retryData;
            }

            console.error("Error updating evolution:", error);
            throw error;
        }
        return data;
    }
};

